type BinaryOption = {
  tokenName: string;
  tokenAddress: string;
};

type BinaryProbabilityConfig<T> = {
  getName?: (option: T) => string;
  getPrice?: (option: T) => string | number;
  getDecimal?: (option: T) => string | number;
};

const normalizeTokenName = (tokenName: string) => tokenName.trim().toLowerCase();

// Resolve the yes/no sides of a binary market. Match by name first, then fall
// back to index position so custom-named binaries (Up/Down, team names) still
// resolve both sides instead of collapsing to undefined — undefined is what
// makes labels show hardcoded YES/NO, charts render empty, and colors drop.
export const getBinaryOptions = <T>(options: T[], getName: (option: T) => string) => {
  let yes = options.find(option => {
    const tokenName = normalizeTokenName(getName(option));
    return tokenName === 'yes' || tokenName === 'option1';
  });

  let no = options.find(option => {
    const tokenName = normalizeTokenName(getName(option));
    return tokenName === 'no' || tokenName === 'option2';
  });

  if (!yes && !no && options.length === 2) {
    yes = options[0];
    no = options[1];
  }

  return { yes, no };
};

const defaultGetName = <T>(option: T) =>
  ((option as { tokenName?: string; optionName?: string }).tokenName ??
    (option as { tokenName?: string; optionName?: string }).optionName ??
    '');

const defaultGetPrice = <T>(option: T) =>
  (option as { tokenPrice?: string | number; price?: string | number }).tokenPrice ??
  (option as { tokenPrice?: string | number; price?: string | number }).price ??
  0;

const defaultGetDecimal = <T>(option: T) =>
  (option as { decimal?: string | number }).decimal ?? 0;

const getDisplayPrice = <T>(
  option: T,
  getPrice: (option: T) => string | number,
  getDecimal: (option: T) => string | number
) => {
  const price = Number(getPrice(option));
  const decimal = Number(getDecimal(option));
  const scale = Math.pow(10, decimal);

  if (!Number.isFinite(price) || !Number.isFinite(scale) || scale <= 0) {
    return null;
  }

  return price / scale;
};

export const getBinaryMarketOptions = <T extends BinaryOption>(options: T[]) =>
  getBinaryOptions(options, option => option.tokenName);

export const getBinaryProbabilityPercentage = <T>(
  options: T[],
  config: BinaryProbabilityConfig<T> = {}
) => {
  const getName = config.getName ?? defaultGetName;
  const getPrice = config.getPrice ?? defaultGetPrice;
  const getDecimal = config.getDecimal ?? defaultGetDecimal;
  const { yes, no } = getBinaryOptions(options, getName);

  if (!yes || !no) return null;

  const yesPrice = getDisplayPrice(yes, getPrice, getDecimal);
  const noPrice = getDisplayPrice(no, getPrice, getDecimal);

  if (yesPrice === null || noPrice === null) return null;

  const total = yesPrice + noPrice;
  if (total <= 0) return 50;

  return Math.min(100, Math.max(0, Math.round((yesPrice / total) * 100)));
};
